// Elements palette.
//
// Floating popover that lets the writer change the current block's type
// without leaving home row. Modeled on the Final Draft "Elements"
// window: a short list of element types, each addressable by a single
// letter. The popover is anchored under the caret (unlike FD's
// always-on-top floating window) so the writer stays in their reading
// flow.
//
// Two trigger paths:
//   1. Cmd-/ from any block         → opens the palette
//   2. `/` typed in an empty block  → opens the palette and consumes
//                                     the `/` (handled by the input
//                                     rule in input-rules.js, which
//                                     dispatches a transaction with
//                                     the OPEN_META meta tag).
//
// Selection paths:
//   - Single letter (A/S/C/P/D/T/H)  → instant assign + close
//   - Arrow Up/Down                  → move highlight
//   - Enter                          → assign highlighted + close
//   - Click                          → assign clicked
//   - Esc / blur                     → close without changing the type

import { Plugin, PluginKey } from 'prosemirror-state';
import { assignType } from './commands.js';

// Order optimized for frequency of use in real screenplays. Single-letter
// shortcuts match the Final Draft Elements palette so muscle memory
// transfers (A=Action, S=Scene, C=Character, P=Paren, D=Dialogue,
// T=Transition, H=sHot — H because S is taken).
export const PALETTE_ELEMENTS = [
  { key: 'A', label: 'Action', storage: 'action', hint: '!' },
  { key: 'S', label: 'Scene Heading', storage: 'scene-heading', hint: '.' },
  { key: 'C', label: 'Character', storage: 'character', hint: '@' },
  { key: 'P', label: 'Parenthetical', storage: 'parenthetical', hint: '(' },
  { key: 'D', label: 'Dialogue', storage: 'dialogue', hint: '' },
  { key: 'T', label: 'Transition', storage: 'transition', hint: '>' },
  { key: 'H', label: 'Shot', storage: 'shot', hint: '' },
];

export const palettePluginKey = new PluginKey('lastslate-element-palette');

// Meta tags accepted by the plugin's appendTransaction-style hook to
// open or close the palette in response to a programmatic event. The
// plain Plugin doesn't expose a state setter, so commands flip the
// open flag via these meta values. Exported so the input-rules plugin
// (which can't import this whole module cycle-free) can dispatch with
// the same string the palette listens for.
export const PALETTE_OPEN_META = 'lastslate-palette-open';
export const PALETTE_CLOSE_META = 'lastslate-palette-close';
const OPEN_META = PALETTE_OPEN_META;
const CLOSE_META = PALETTE_CLOSE_META;

export function openPaletteCommand(state, dispatch) {
  if (dispatch) {
    dispatch(state.tr.setMeta(palettePluginKey, { type: OPEN_META }));
  }
  return true;
}

function currentBlockStorage(state) {
  const $from = state.selection.$from;
  if ($from.depth < 1) return null;
  const name = $from.node(1).type.name;
  // Schema uses underscore (scene_heading); palette / assignType use kebab.
  return name === 'scene_heading' ? 'scene-heading' : name;
}

function rectAfterCursor(view) {
  const $from = view.state.selection.$from;
  if ($from.depth < 1) return null;
  const blockPos = $from.before(1);
  let dom;
  try {
    dom = view.nodeDOM(blockPos);
  } catch {
    return null;
  }
  if (!dom || !dom.getBoundingClientRect) return null;
  return dom.getBoundingClientRect();
}

class PaletteView {
  constructor(view) {
    this.view = view;
    this.open = false;
    this.activeIdx = 0;
    this.el = document.createElement('div');
    this.el.className = 'pm-element-palette';
    this.el.style.display = 'none';
    document.body.appendChild(this.el);
    this.onClick = this.onClick.bind(this);
    this.onScrollOrResize = this.onScrollOrResize.bind(this);
    this.el.addEventListener('mousedown', this.onClick);
    window.addEventListener('scroll', this.onScrollOrResize, true);
    window.addEventListener('resize', this.onScrollOrResize);
  }

  onScrollOrResize() {
    if (!this.open) return;
    this.position();
  }

  onClick(e) {
    const item = e.target.closest('[data-storage]');
    if (!item) return;
    e.preventDefault(); // mousedown — keep editor focus
    this.accept(item.dataset.storage);
  }

  show(view) {
    this.view = view;
    this.open = true;
    const curStorage = currentBlockStorage(view.state);
    const idx = PALETTE_ELEMENTS.findIndex((el) => el.storage === curStorage);
    this.activeIdx = idx >= 0 ? idx : 0;
    this.el.style.display = 'block';
    this.render();
    this.position();
  }

  hide() {
    if (!this.open) return;
    this.open = false;
    this.el.style.display = 'none';
    this.view.dispatch(this.view.state.tr.setMeta(palettePluginKey, { type: CLOSE_META }));
  }

  accept(storage) {
    if (!storage) return;
    const view = this.view;
    // Close first so the dispatched type-change doesn't re-trigger show().
    this.open = false;
    this.el.style.display = 'none';
    assignType(storage)(view.state, view.dispatch);
    view.focus();
    view.dispatch(view.state.tr.setMeta(palettePluginKey, { type: CLOSE_META }));
  }

  render() {
    const curStorage = currentBlockStorage(this.view.state);
    this.el.innerHTML = PALETTE_ELEMENTS.map((el, i) => {
      const active = i === this.activeIdx ? ' active' : '';
      const current = el.storage === curStorage ? ' is-current' : '';
      const hint = el.hint ? `<span class="pm-palette-hint">${el.hint}</span>` : '';
      return (
        `<div class="pm-palette-item${active}${current}" data-storage="${el.storage}">` +
        `<span class="pm-palette-key">${el.key}</span>` +
        `<span class="pm-palette-label">${el.label}</span>` +
        hint +
        `</div>`
      );
    }).join('');
  }

  position() {
    const rect = rectAfterCursor(this.view);
    if (!rect) {
      this.hide();
      return;
    }
    const vh = window.innerHeight;
    if (rect.bottom < 0 || rect.top > vh) {
      this.hide();
      return;
    }
    this.el.style.top = `${rect.bottom + window.scrollY + 4}px`;
    this.el.style.left = `${rect.left + window.scrollX}px`;
  }

  destroy() {
    this.el.removeEventListener('mousedown', this.onClick);
    window.removeEventListener('scroll', this.onScrollOrResize, true);
    window.removeEventListener('resize', this.onScrollOrResize);
    if (this.el.parentNode) this.el.parentNode.removeChild(this.el);
  }
}

export function elementPalettePlugin() {
  let palette = null;
  return new Plugin({
    key: palettePluginKey,
    view(editorView) {
      palette = new PaletteView(editorView);
      return {
        update(view, oldState) {
          if (!palette) return;
          // React to OPEN/CLOSE metas dispatched this turn.
          const tr = view.state.tr;
          for (const t of [tr]) void t; // (no-op; access state.tr below)
          // The plugin can't see the just-dispatched transaction directly
          // through `update`, so we infer state from the selection: if the
          // palette is open but the selection moved to a different block,
          // close it. The OPEN/CLOSE metas are observed via a separate
          // appendTransaction below.
          if (palette.open) {
            const oldBlockPos = safeBlockPos(oldState);
            const newBlockPos = safeBlockPos(view.state);
            if (oldBlockPos !== newBlockPos) {
              palette.hide();
              return;
            }
            palette.view = view;
            palette.position();
          }
        },
        destroy() {
          if (palette) {
            palette.destroy();
            palette = null;
          }
        },
      };
    },
    appendTransaction(transactions, _oldState, _newState) {
      // Detect OPEN/CLOSE metas and toggle the view. We don't actually
      // need to append a transaction — this hook is just a convenient
      // place to observe transaction metas before they're committed.
      for (const t of transactions) {
        const meta = t.getMeta(palettePluginKey);
        if (!meta) continue;
        // Use setTimeout(0) so show() runs after PM finishes applying the
        // transaction — otherwise the view's positioning would read a
        // stale selection.
        if (meta.type === OPEN_META && palette && !palette.open) {
          setTimeout(() => {
            if (palette) palette.show(palette.view);
          }, 0);
        } else if (meta.type === CLOSE_META && palette && palette.open) {
          palette.open = false;
          palette.el.style.display = 'none';
        }
      }
      return null;
    },
    props: {
      handleKeyDown(_view, event) {
        if (!palette || !palette.open) return false;
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            palette.activeIdx = (palette.activeIdx + 1) % PALETTE_ELEMENTS.length;
            palette.render();
            return true;
          case 'ArrowUp':
            event.preventDefault();
            palette.activeIdx =
              (palette.activeIdx - 1 + PALETTE_ELEMENTS.length) % PALETTE_ELEMENTS.length;
            palette.render();
            return true;
          case 'Enter': {
            event.preventDefault();
            const pick = PALETTE_ELEMENTS[palette.activeIdx];
            if (pick) palette.accept(pick.storage);
            return true;
          }
          case 'Escape':
            event.preventDefault();
            palette.hide();
            return true;
          default: {
            // Single-letter shortcut. Letter keys produce one-char `key`
            // values; modifier-prefixed shortcuts come through with the
            // letter too but we let the editor handle those.
            if (event.ctrlKey || event.metaKey || event.altKey) return false;
            if (typeof event.key !== 'string' || event.key.length !== 1) return false;
            const upper = event.key.toUpperCase();
            const match = PALETTE_ELEMENTS.find((el) => el.key === upper);
            if (!match) return false;
            event.preventDefault();
            palette.accept(match.storage);
            return true;
          }
        }
      },
    },
  });
}

function safeBlockPos(state) {
  const $from = state.selection.$from;
  if ($from.depth < 1) return -1;
  return $from.before(1);
}
